import { useCallback, useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp } from "lucide-react";
import { GAME_LEVELS } from "../constants/gameLevels";
import {
  getHistoryFilterEmptyMessage,
  HISTORY_FILTER_ALL,
  resolveHistoryEntryType,
} from "../constants/levelHistory";
import { formatSessionDate, formatSessionTime } from "../utils/parisWallClock";
import CaptureIcon from "./IconComponent/CaptureIcon";
import QuestIcon from "./IconComponent/QuestIcon";

const publicUrl = process.env.PUBLIC_URL || "";

const WORD_CAPTURE_MESSAGE =
  "Bon travail ! Vous avez capturé 1 nouveau mot !";

function getMasteryLevelById(levelId) {
  return GAME_LEVELS.find((level) => level.id === levelId) || null;
}

function buildMasteryMessage(direction, levelName) {
  if (direction === "down") {
    return `Vous régressez d'un niveau et êtes désormais ${levelName}.`;
  }
  return `Vous passez au niveau supérieur et êtes désormais ${levelName}.`;
}

function MasteryLevelIcon({ levelId }) {
  const level = getMasteryLevelById(levelId);
  if (!level) return null;

  return (
    <img
      src={`${publicUrl}/icons/${level.icon}`}
      alt=""
      className="level-history__level-icon"
      loading="lazy"
      draggable={false}
      aria-hidden="true"
    />
  );
}

function CaptureEntryVisual() {
  return (
    <div
      className="level-history__entry-levels level-history__entry-levels--capture"
      aria-hidden="true"
    >
      <CaptureIcon size={42} color="#39210d" strokeWidth={1} />
      <span className="level-history__capture-bonus">+1</span>
    </div>
  );
}

function DailyQuestEntryVisual() {
  return (
    <div
      className="level-history__entry-levels level-history__entry-levels--quest"
      aria-hidden="true"
    >
      <QuestIcon size={42} color="#39210d" strokeWidth={1} />
    </div>
  );
}

function LevelHistoryEntryVisual({ entry, entryType }) {
  if (entryType === "word_capture") {
    return <CaptureEntryVisual />;
  }

  if (entryType === "daily_quest") {
    return <DailyQuestEntryVisual />;
  }

  const ArrowIcon = entry.direction === "down" ? ArrowDown : ArrowUp;

  return (
    <div className="level-history__entry-levels" aria-hidden="true">
      <MasteryLevelIcon levelId={entry.fromLevelId} />
      <ArrowIcon
        size={22}
        strokeWidth={2.25}
        className={`level-history__arrow level-history__arrow--${entry.direction}`}
      />
      <MasteryLevelIcon levelId={entry.toLevelId} />
    </div>
  );
}

const HISTORY_SKELETON_ROWS = [
  {
    type: "quest",
    date: "05/09/2026 à 00:38",
    message: "Félicitation, vous avez accompli la quête journalière !",
  },
  {
    type: "capture",
    date: "04/09/2026 à 22:14",
    message: "Bon travail ! Vous avez capturé 1 nouveau mot !",
  },
  {
    type: "capture",
    date: "03/09/2026 à 18:06",
    message: "Bon travail ! Vous avez capturé 1 nouveau mot !",
  },
  {
    type: "capture",
    date: "02/09/2026 à 11:42",
    message: "Bon travail ! Vous avez capturé 1 nouveau mot !",
  },
  {
    type: "capture",
    date: "01/09/2026 à 09:17",
    message: "Bon travail ! Vous avez capturé 1 nouveau mot !",
  },
  {
    type: "capture",
    date: "31/08/2026 à 16:55",
    message: "Bon travail ! Vous avez capturé 1 nouveau mot !",
  },
];

function LevelHistorySkeleton() {
  return (
    <div className="level-history level-history--skeleton" aria-hidden="true">
      <div className="level-history__list-shell">
        <div className="level-history__list">
          {HISTORY_SKELETON_ROWS.map((row, index) => (
            <article key={index} className="level-history__entry">
              <div className="level-history__entry-main">
                <time className="level-history__entry-date">
                  {row.date}
                  <span className="level-history-skel level-history-skel--date" />
                </time>
                <p className="level-history__entry-message">
                  {row.message}
                  <span className="level-history-skel level-history-skel--message" />
                </p>
              </div>
              {row.type === "quest" ? (
                <DailyQuestEntryVisual />
              ) : (
                <CaptureEntryVisual />
              )}
            </article>
          ))}
        </div>
      </div>
    </div>
  );
}

function LevelHistoryEntry({ entry }) {
  const entryType = resolveHistoryEntryType(entry);
  let message = entry.message || "";

  if (!message) {
    if (entryType === "word_capture") {
      message = WORD_CAPTURE_MESSAGE;
    } else if (entryType === "daily_quest") {
      message =
        "Félicitation, vous avez accompli la quête journalière !";
    } else {
      const targetLevel = getMasteryLevelById(entry.toLevelId);
      const levelName =
        entry.toLevelName || targetLevel?.name || `Niveau ${entry.toLevelId}`;
      message = buildMasteryMessage(entry.direction, levelName);
    }
  }

  return (
    <article
      className={`level-history__entry${
        entry.read ? "" : " level-history__entry--unread"
      }`}
    >
      <div className="level-history__entry-main">
        <time
          className="level-history__entry-date"
          dateTime={entry.playedAt || `${entry.date} ${entry.time}`}
        >
          {formatSessionDate(entry)} à {formatSessionTime(entry)}
        </time>
        <p className="level-history__entry-message">{message}</p>
      </div>

      <LevelHistoryEntryVisual entry={entry} entryType={entryType} />
    </article>
  );
}

export default function LevelHistoryView({
  entries = [],
  loading = false,
  error = "",
  filter = HISTORY_FILTER_ALL,
}) {
  const listRef = useRef(null);
  const [scrollFades, setScrollFades] = useState({ top: false, bottom: false });

  const updateScrollFades = useCallback(() => {
    const el = listRef.current;
    if (!el) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    const threshold = 6;

    setScrollFades({
      top: scrollTop > threshold,
      bottom: scrollTop + clientHeight < scrollHeight - threshold,
    });
  }, []);

  useEffect(() => {
    updateScrollFades();
  }, [entries, updateScrollFades]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return undefined;

    const observer = new ResizeObserver(updateScrollFades);
    observer.observe(el);
    return () => observer.disconnect();
  }, [entries, updateScrollFades]);

  if (loading) {
    return <LevelHistorySkeleton />;
  }

  if (error) {
    return (
      <div className="level-history">
        <p className="level-history__status level-history__status--error" role="alert">
          {error}
        </p>
      </div>
    );
  }

  if (!entries.length) {
    return (
      <div className="level-history">
        <p className="level-history__status">{getHistoryFilterEmptyMessage(filter)}</p>
      </div>
    );
  }

  return (
    <div className="level-history">
      <div
        className={[
          "level-history__list-shell",
          scrollFades.top && "level-history__list-shell--fade-top",
          scrollFades.bottom && "level-history__list-shell--fade-bottom",
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div
          ref={listRef}
          className="level-history__list"
          role="log"
          aria-label="Observateur d'évènements : passages de niveau et captures de mots"
          onScroll={updateScrollFades}
        >
          {entries.map((entry) => (
            <LevelHistoryEntry key={entry.id} entry={entry} />
          ))}
        </div>
      </div>
    </div>
  );
}
